import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { CreditUpdate, UserProfile } from '../types';
import { useTranslation } from '../lib/translations';
import { X, History, Loader2, ArrowUpRight, ArrowDownRight, Info } from 'lucide-react';
import { format } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';

interface CreditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  teacherId: string;
  studentId: string;
}

const CreditHistoryModal: React.FC<CreditHistoryModalProps> = ({ isOpen, onClose, teacherId, studentId }) => {
  const { t, language } = useTranslation();
  const [updates, setUpdates] = useState<CreditUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const locale = language === 'fr' ? fr : enUS;

  useEffect(() => {
    if (!isOpen) return;

    const fetchHistory = async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, 'credit_updates'),
          where('teacherId', '==', teacherId),
          where('studentId', '==', studentId),
          orderBy('timestamp', 'desc'),
          limit(50)
        );
        const snap = await getDocs(q);
        setUpdates(snap.docs.map(d => ({ id: d.id, ...d.data() } as CreditUpdate)));
      } catch (e) {
        console.error("Failed to fetch credit history:", e);
      }
      setLoading(false);
    };

    fetchHistory();
  }, [isOpen, teacherId, studentId]);

  if (!isOpen) return null;

  const getTypeLabel = (type: CreditUpdate['type']) => {
    switch (type) {
      case 'manual': return t.studentDetail.typeManual;
      case 'pack': return t.studentDetail.typePack;
      case 'lesson_completion': return t.studentDetail.typeLesson;
      case 'sync': return t.studentDetail.typeSync;
      default: return type;
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6 bg-slate-950/70 backdrop-blur-md animate-in fade-in duration-300 !mt-0">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl h-[80vh] rounded-2xl border border-slate-200 dark:border-white/5 shadow-2xl flex flex-col overflow-hidden animate-in zoom-in duration-300">
        
        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-slate-100 dark:border-white/5 flex items-start justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 text-purple-600 rounded-xl flex items-center justify-center">
              <History size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">{t.studentDetail.creditHistory}</h2>
              <p className="text-slate-500 text-xs font-medium">{t.studentDetail.creditHistoryDesc}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all active:scale-90">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 custom-scrollbar">
          {loading ? (
            <div className="h-full flex flex-col items-center justify-center gap-4">
              <Loader2 className="animate-spin text-purple-600" size={32} />
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{t.common.loading}</p>
            </div>
          ) : updates.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-50 py-20">
              <History size={48} className="text-slate-300 dark:text-slate-700 mb-4" />
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest italic">{t.studentDetail.noCreditHistory}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-white/5">
                    <th className="py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">{t.common.date}</th>
                    <th className="py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">{t.common.type}</th>
                    <th className="py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">{t.studentDetail.changeAmount}</th>
                    <th className="py-4 text-[10px] font-black uppercase tracking-widest text-slate-400 text-right">{t.studentDetail.balance}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-white/5">
                  {updates.map((update) => (
                    <tr key={update.id} className="group hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                      <td className="py-4 pr-4">
                        <p className="text-xs font-bold text-slate-700 dark:text-slate-300 leading-tight">
                          {format(update.timestamp.toDate(), 'PPP', { locale })}
                        </p>
                        <p className="text-[9px] font-medium text-slate-400 tracking-tighter">
                          {format(update.timestamp.toDate(), 'p', { locale })}
                        </p>
                      </td>
                      <td className="py-4">
                        <div className="space-y-1">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-900 dark:text-slate-100">
                            {getTypeLabel(update.type)}
                          </span>
                          {update.note && (
                            <p className="text-[9px] font-medium text-slate-500 italic max-w-[150px] truncate" title={update.note}>
                              {update.note}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-4 text-right font-black text-sm">
                        <div className={`flex items-center justify-end gap-1 ${update.change > 0 ? 'text-teal-600' : 'text-red-500'}`}>
                          {update.change > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                          {update.change > 0 ? '+' : ''}{update.change}
                        </div>
                      </td>
                      <td className="py-4 text-right font-black text-slate-900 dark:text-white text-sm">
                        {update.balanceAfter}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pt-8 flex items-center justify-center gap-2 opacity-50">
                <Info size={14} className="text-slate-400" />
                <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                  Showing last 50 updates
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-slate-950 flex justify-end shrink-0">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/5 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:border-purple-500/30 transition-all shadow-sm"
          >
            {t.common.done}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreditHistoryModal;